/**
 * Auto-save hook for web app
 *
 * Automatically saves the project to IndexedDB when changes are detected.
 * Saves after a debounce period to avoid excessive writes.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { saveProjectToDB } from '@/utils/indexedDB';
import { imageStore } from '@/utils/browserAPI';

const AUTO_SAVE_DELAY = 2000; // 2 seconds after last change
const AUTO_SAVE_PROJECT_ID = 'autosave'; // Fixed ID for auto-saved project
const ERROR_DISPLAY_DURATION = 5000; // Show error for 5 seconds

export function useAutoSave() {
  const projectName = useProjectStore((state) => state.projectName);
  const isDirty = useProjectStore((state) => state.isDirty);
  const getProjectData = useProjectStore((state) => state.getProjectData);
  // Subscribe to data changes to trigger re-save even when isDirty stays true
  const nodes = useProjectStore((state) => state.nodes);
  const titleNodes = useProjectStore((state) => state.titleNodes);
  const commentBoxes = useProjectStore((state) => state.commentBoxes);
  const referenceNodes = useProjectStore((state) => state.referenceNodes);

  const saveTimeoutRef = useRef<number | null>(null);
  const lastSaveRef = useRef<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const errorTimeoutRef = useRef<number | null>(null);

  /**
   * Manual save function - can be called to trigger immediate save
   */
  const saveNow = useCallback(async () => {
    // Only save if we have a named project
    if (projectName === 'Untitled Project') {
      return false;
    }

    try {
      // Use getProjectData() to ensure ALL project fields are saved
      // This includes: nodes, referenceNodes, noteNodes, commentBoxes,
      // sceneHeadingHistory, tagHistory, aspectRatio, beatColorScheme, etc.
      const projectData = getProjectData();

      await saveProjectToDB(
        AUTO_SAVE_PROJECT_ID,
        projectName,
        projectData,
        imageStore
      );

      // Update last save reference
      lastSaveRef.current = JSON.stringify(projectData);

      // Clear any previous errors
      setSaveError(null);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }

      console.log('[AutoSave] Manual save completed successfully');
      return true;
    } catch (error) {
      console.error('[AutoSave] Manual save failed:', error);

      // Set error message for user notification
      const errorMessage = (error as Error).message || 'Failed to save project';
      setSaveError(errorMessage);

      // Clear error message after duration
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      errorTimeoutRef.current = window.setTimeout(() => {
        setSaveError(null);
      }, ERROR_DISPLAY_DURATION);

      return false;
    }
  }, [projectName, getProjectData]);

  useEffect(() => {
    // Only auto-save if there are changes and we have a named project
    if (!isDirty || projectName === 'Untitled Project') {
      return;
    }

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Schedule auto-save after delay
    // We capture fresh data INSIDE the timeout to ensure we save the latest state
    saveTimeoutRef.current = window.setTimeout(async () => {
      try {
        // Get FRESH project data at save time, not at schedule time
        const freshProjectData = getProjectData();
        const freshSnapshot = JSON.stringify(freshProjectData);

        // Only save if something actually changed from last saved state
        if (freshSnapshot === lastSaveRef.current) {
          return;
        }

        await saveProjectToDB(
          AUTO_SAVE_PROJECT_ID,
          projectName,
          freshProjectData,
          imageStore
        );

        lastSaveRef.current = freshSnapshot;

        // Clear any previous errors
        setSaveError(null);
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = null;
        }
      } catch (error) {
        console.error('Auto-save failed:', error);

        // Set error message for user notification
        const errorMessage = (error as Error).message || 'Failed to auto-save project';
        setSaveError(errorMessage);

        // Clear error message after duration
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
        }
        errorTimeoutRef.current = window.setTimeout(() => {
          setSaveError(null);
        }, ERROR_DISPLAY_DURATION);

        // Show alert for quota errors (critical)
        if (errorMessage.includes('quota')) {
          alert(`Auto-save failed: ${errorMessage}\n\nPlease save your work manually.`);
        }
      }
    }, AUTO_SAVE_DELAY);

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [projectName, isDirty, getProjectData, nodes, titleNodes, commentBoxes, referenceNodes]);

  // Return error state and manual save function
  return { saveError, saveNow };
}
